/**
 * Mixpanel client for web — lazy-loaded via dynamic import().
 * Events fired before the library loads are queued and flushed once ready.
 * EU API host for eu.mixpanel.com projects.
 */

import { logger } from '../helpers/logger.helper'
import { getAnalyticsEnvironment } from './environment.helper'

const MIXPANEL_EU_API_HOST = 'https://api-eu.mixpanel.com'

type MixpanelBrowser = typeof import('mixpanel-browser').default

let instance: MixpanelBrowser | null = null
let loading = false
let hasFailed = false

const MAX_QUEUE_SIZE = 500

type QueuedCall =
  | { type: 'track'; args: [string, Record<string, unknown>?] }
  | { type: 'identify'; args: [string] }
  | { type: 'people.set'; args: [Record<string, unknown>] }
  | { type: 'reset'; args: [] }

const queue: QueuedCall[] = []

function flushQueue(mx: MixpanelBrowser): void {
  while (queue.length > 0) {
    const call = queue.shift()!
    switch (call.type) {
      case 'track': {
        const [event, props] = call.args
        mx.track(event, props)
        break
      }
      case 'identify': {
        const [id] = call.args
        mx.identify(id)
        break
      }
      case 'people.set': {
        const [props] = call.args
        mx.people.set(props)
        break
      }
      case 'reset':
        mx.reset()
        break
    }
  }
}

export function initMixpanelWeb(token: string): void {
  if (instance || loading) return
  if (!token) {
    logger.debug('[Analytics] Mixpanel (web) init skipped: no token')
    return
  }
  loading = true
  import('mixpanel-browser')
    .then((mod) => {
      const mixpanel = mod.default ?? mod
      mixpanel.init(token, {
        api_host: MIXPANEL_EU_API_HOST,
        ignore_dnt: true,
        persistence: 'localStorage',
        track_pageview: false,
      })
      const env = getAnalyticsEnvironment()
      mixpanel.register({ environment: env })
      instance = mixpanel
      loading = false
      logger.debug('[Analytics] Mixpanel (web) ready', { environment: env })
      if (env === 'development') {
        mixpanel.track('zporti_dev_ready', {
          environment: env,
          origin: typeof window !== 'undefined' ? window.location?.origin : undefined,
        })
      }
      flushQueue(mixpanel)
    })
    .catch((e: Error) => {
      loading = false
      hasFailed = true
      queue.length = 0
      logger.warn('[Analytics] Mixpanel (web) init failed', { message: e.message })
    })
}

export function getMixpanelWeb(): MixpanelBrowser | null {
  return instance
}

export function isMixpanelWebInitialized(): boolean {
  return instance !== null
}

/**
 * Queue-aware track: sends immediately if loaded, queues otherwise.
 */
export function mixpanelTrack(event: string, properties?: Record<string, unknown>): void {
  if (hasFailed) return
  if (instance) {
    instance.track(event, properties)
  } else {
    if (queue.length >= MAX_QUEUE_SIZE) queue.shift()
    queue.push({ type: 'track', args: [event, properties] })
  }
}

/**
 * Queue-aware identify: sends immediately if loaded, queues otherwise.
 */
export function mixpanelIdentify(distinctId: string, properties?: Record<string, unknown>): void {
  if (hasFailed) return
  if (instance) {
    instance.identify(distinctId)
    if (properties && Object.keys(properties).length > 0) instance.people.set(properties)
  } else {
    if (queue.length >= MAX_QUEUE_SIZE) queue.shift()
    queue.push({ type: 'identify', args: [distinctId] })
    if (properties && Object.keys(properties).length > 0) {
      if (queue.length >= MAX_QUEUE_SIZE) queue.shift()
      queue.push({ type: 'people.set', args: [properties] })
    }
  }
}

/**
 * Queue-aware reset: sends immediately if loaded, queues otherwise.
 */
export function mixpanelReset(): void {
  if (hasFailed) return
  if (instance) {
    instance.reset()
  } else {
    if (queue.length >= MAX_QUEUE_SIZE) queue.shift()
    queue.push({ type: 'reset', args: [] })
  }
}
